import { Divider, Button } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { TFunction } from "i18next";
import { TabsField } from "./editTabs";
import formStyles from "@/components/DynamicForm/index.module.less";
import editStyles from "./edit.style.module.less";

// ==================== 类型定义 ====================
interface EvaluationSummary {
  poorCount?: number;
  middleCount?: number;
  excellentCount?: number;
}

// ==================== 评估表单配置 ====================
export const getEvaluationFormItems = (
  t: TFunction,
  isViewOnly: boolean = false,
  formData?: {
    evaluationData?: any;
    resultDetailsList?: Array<{
      phaseName?: string;
      excellentNum?: number;
      middleNum?: number;
      poorNum?: number;
      evaluationNames?: string[];
    }>;
    isFromGm?: boolean; // 是否来自GM
  },
  evaluationSummary?: EvaluationSummary,
  currentPersonFlag: boolean = false,
  onOpenQRCodeModal?: () => void,
  isApprovalInProgress: boolean = false,
  isFromVsmc: boolean = false,
  isFromGm: boolean = false, // 新增参数：是否来自GM工作台
  onRefreshSignature?: () => void, // 新增参数：刷新签批意见回调
) => [
  {
    name: "custom1",
    col: { span: 24 },
    custom: {
      render: () => (
        <div className={formStyles.sectionHeader}>
          <div className={formStyles.sectionIndicator} />
          <span className={formStyles.sectionTitle}>{t("申请人信息")}</span>
        </div>
      ),
    },
  },
  {
    name: "applicantWrapperStart",
    col: { span: 24 },
    wrapperStart: { className: formStyles.wrapperPadded },
  },
  {
    label: t("员工姓名"),
    name: "purchaseUserNickname",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [{ message: t("请输入员工姓名") }],
    widget: {
      widgetType: "Input",
      disabled: true,
    },
  },
  {
    label: t("员工工号"),
    name: "purchaseUserUsername",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [{ message: t("请输入员工工号") }],
    widget: {
      widgetType: "Input",
      disabled: true,
    },
  },
  {
    label: t("评估日期"),
    name: "createTime",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    widget: {
      widgetType: "DatePicker",
      style: { width: "100%" },
      disabled: true,
    },
  },
  {
    label: t("部门"),
    name: "purchaseUserDeptName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [],
    widget: {
      widgetType: "Input",
      disabled: true,
    },
  },
  {
    name: "purchaseUserDeptNo",
    col: { span: 0 },
    widget: {
      widgetType: "Input",
      hidden: true,
    },
  },
  {
    label: t("岗位"),
    name: "purchaseUserPostName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [],
    widget: {
      widgetType: "Input",
      disabled: true,
    },
  },
  {
    name: "purchaseUserPostNo",
    col: { span: 0 },
    widget: {
      widgetType: "Input",
      hidden: true,
    },
  },
  {
    label: t("邮箱"),
    name: "purchaseUserEmail",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [{ type: "email", message: t("邮箱格式不正确") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入"),
    },
  },
  {
    name: "applicantWrapperEnd",
    col: { span: 24 },
    wrapperEnd: true,
  },
  {
    name: "custom2",
    col: { span: 24 },
    custom: {
      render: () => (
        <div className={formStyles.sectionHeaderWithAction}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div className={formStyles.sectionIndicator} />
            <span className={formStyles.sectionTitle}>
              {t("供应商基本信息")}
            </span>
          </div>
        </div>
      ),
    },
  },
  {
    name: "supplierWrapperStart",
    col: { span: 24 },
    wrapperStart: { className: formStyles.wrapperLight },
  },
  {
    label: t("供应商名称"),
    name: "supplierUserName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请输入供应商名称") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入供应商名称"),
      disabled: isViewOnly,
    },
  },
  {
    label: t("供应商状态"),
    name: "supplierStatusName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请选择") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入"),
      disabled: isViewOnly,
    },
  },
  {
    label: t("物料状态"),
    name: "materialStatusName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请选择") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入"),
      disabled: isViewOnly,
    },
  },
  {
    label: t("物料用途类别"),
    name: "materialCategoryName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请选择") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入"),
      disabled: isViewOnly,
    },
  },
  {
    label: t("物料编号如有"),
    name: "materialCode",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    widget: {
      widgetType: "Input",
      placeholder: t("请输入物料编号"),
      disabled: isViewOnly,
    },
  },
  {
    label: t("物料名称"),
    name: "materialName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请输入") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入物料名称"),
      disabled: isViewOnly,
    },
  },
  {
    label: t("注册市场"),
    name: "businessRegistration",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [{ required: true, message: t("请选择") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请选择"),
      disabled: isViewOnly,
    },
  },
  {
    label: t("产地区域"),
    name: "productionArea",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [{ required: true, message: t("请选择") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请选择"),
      disabled: isViewOnly,
    },
  },
  {
    name: "applicantWrapperEnd",
    col: { span: 24 },
    wrapperEnd: true,
  },
  {
    name: "assessmentTabs",
    col: { span: 24 },
    custom: {
      render: () => (
        <TabsField evaluationSummary={evaluationSummary} formData={formData} />
      ),
    },
  },
  // ==================== VSMC会议评审部分（仅当 currentPersonFlag === true 或 isFromGm === true 或 isViewOnly === true 且不是已通过或已驳回状态时显示） ====================
  ...(() => {
    const shouldShow =
      (currentPersonFlag || isFromGm || isViewOnly) &&
      formData?.evaluationStatus !== 4 &&
      formData?.evaluationStatus !== 3 &&
      !(formData?.evaluationStatus === 2 && isViewOnly);

    return shouldShow
      ? [
          {
            name: "divider1",
            col: { span: 24 },
            custom: {
              render: () => <Divider style={{ margin: "24px 0" }} />,
            },
          },
          {
            name: "custom3",
            col: { span: 24 },
            custom: {
              render: () => (
                <div className={formStyles.sectionHeaderWithActionNoBg}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div className={formStyles.sectionIndicator} />
                    <span className={formStyles.sectionTitle}>
                      {t("VSMC会议评审")}
                    </span>
                  </div>
                  {!isFromGm && !isViewOnly && (
                    <div
                      className={formStyles.sectionAction}
                      onClick={() => {
                        onOpenQRCodeModal?.();
                      }}
                    >
                      <span
                        className={`${formStyles.actionText} ${editStyles.qrcodeActionText}`}
                      >
                        {t("发起二维码审批")}
                      </span>
                    </div>
                  )}
                </div>
              ),
            },
          },
          {
            label: t("审核意见"),
            name: "reviewOpinion",
            col: { span: 24 },
            labelCol: { span: 3, style: { color: "#000000" } },
            wrapperCol: { span: 20 },
            className: `inlineLabel ${editStyles.reviewCommentsLabel}`,
            rules: [
              {
                required: isApprovalInProgress || isFromVsmc || isFromGm,
                message: t("请选择审核意见"),
              },
            ],
            widget: {
              widgetType: "Radio.Group",
              options: isFromVsmc
                ? [
                    { label: t("需呈GM决策"), value: "toGm" },
                    { label: t("同意"), value: "agree" },
                    { label: t("驳回"), value: "reject" },
                  ]
                : isFromGm
                  ? [
                      { label: t("同意"), value: "agree" },
                      { label: t("驳回"), value: "reject" },
                    ]
                  : [
                      { label: t("需呈GM决策"), value: "toGm" },
                      { label: t("同意"), value: "agree" },
                      { label: t("驳回"), value: "reject" },
                    ],
              disabled: isViewOnly && !isFromGm,
            },
          },
          {
            label: t("意见说明"),
            name: "reviewComments",
            col: { span: 24 },
            labelCol: { span: 3 },
            wrapperCol: { span: 22 },
            className: `inlineLabel ${editStyles.reviewCommentsLabel}`,
            rules: [
              {
                required: isApprovalInProgress || isFromGm,
                message: t("请输入意见说明"),
              },
            ],
            widget: {
              widgetType: "Input.TextArea",
              placeholder: t("请输入"),
              rows: 4,
              style: { resize: "none" },
              disabled: isViewOnly && !isFromGm,
            },
          },
        ]
      : [];
  })(),
  // ==================== 最终评审人签批（仅当存在 srmSupplierAccessEvaluationFiles 时显示） ====================
  ...(formData?.srmSupplierAccessEvaluationFiles &&
  Array.isArray(formData.srmSupplierAccessEvaluationFiles) &&
  formData.srmSupplierAccessEvaluationFiles.length > 0
    ? [
        {
          name: "signatureSection",
          col: { span: 24 },
          labelCol: { span: 6 },
          wrapperCol: { span: 22 },
          className: "inlineLabel",
          custom: {
            render: (value: any) => (
              <div style={{ marginLeft: 16 }}>
                <div className={editStyles.signatureTitleRow}>
                  <span className={editStyles.signatureTitleText}>
                    {t("评审人签批意见")}
                  </span>
                  {!isFromGm && (
                    <Button
                      className={editStyles.signatureRefreshButton}
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        onRefreshSignature?.();
                      }}
                    >
                      <span className={editStyles.signatureButtonText}>
                        {t("刷新")}
                      </span>
                    </Button>
                  )}
                </div>
                <div className={editStyles.reviewersGridContainer}>
                  {formData.srmSupplierAccessEvaluationFiles.map(
                    (file: any, index: number) => (
                      <div
                        key={file.id || index}
                        className={editStyles.reviewerItem}
                      >
                        <div className={editStyles.reviewerMainRow}>
                          <div className={editStyles.reviewerLeftSection}>
                            <div className={editStyles.reviewerLabel}>
                              {file.approverName}：
                            </div>
                            {file.fileBase64Data ? (
                              <img
                                src={
                                  file.fileBase64Data.startsWith("data:")
                                    ? file.fileBase64Data
                                    : `data:image/png;base64,${file.fileBase64Data.replace(/\s/g, "")}`
                                }
                                alt={file.approverName}
                                className={editStyles.signatureImage}
                                style={{ maxWidth: "80px", maxHeight: "40px" }}
                                onError={(e) => {
                                  console.error(
                                    "签名图片加载失败:",
                                    file.approverName,
                                  );
                                }}
                              />
                            ) : (
                              <div
                                className={editStyles.noSignaturePlaceholder}
                              >
                                {t("未签批")}
                              </div>
                            )}
                          </div>
                          <div className={editStyles.reviewerOpinionWrapper}>
                            <span className={editStyles.opinionLabelInline}>
                              {t("评审意见")}：
                            </span>
                            <span
                              className={editStyles.opinionText}
                              style={{
                                color:
                                  file.opinion === "approved"
                                    ? "#00B42A"
                                    : file.opinion === "rejected"
                                      ? "#E80000"
                                      : undefined,
                              }}
                            >
                              {file.opinion === "approved"
                                ? t("建议准入")
                                : file.opinion === "rejected"
                                  ? t("不建议准入")
                                  : t("暂无意见")}
                            </span>
                          </div>
                        </div>
                        <div className={editStyles.reviewerRemarkRow}>
                          {file.remark || t("暂无备注")}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ),
          },
        },
      ]
    : []),
];
